/**
 * MaternityForm.jsx
 * 
 * Multi-step wizard for creating/editing maternity leave records.
 * Styled with MUI components matching existing patterns.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../axiosConfig';
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    Button,
    Paper,
    CircularProgress,
    Alert,
    Stepper,
    Step,
    StepLabel,
    TextField,
    MenuItem,
    Grid,
    Divider
} from '@mui/material';
import {
    ChildCare as MaternityIcon,
    ArrowBack as BackIcon,
    ArrowForward as NextIcon,
    Check as CheckIcon,
    Person as PersonIcon,
    Event as EventIcon,
    Notifications as NotifyIcon,
    PlayArrow as StartIcon,
    Summarize as SummaryIcon
} from '@mui/icons-material';

const MaternityForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = Boolean(id);

    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [calculatedDates, setCalculatedDates] = useState(null);
    const [error, setError] = useState(null);

    const [formData, setFormData] = useState({
        employee_id: '',
        ewc: '',
        notification_date: '',
        intended_start_date: '',
        actual_birth_date: ''
    });

    const steps = [
        { label: 'Select Employee', icon: <PersonIcon /> },
        { label: 'Expected Week', icon: <EventIcon /> },
        { label: 'Notification Date', icon: <NotifyIcon /> },
        { label: 'Start Date', icon: <StartIcon /> },
        { label: 'Review & Confirm', icon: <SummaryIcon /> }
    ];

    useEffect(() => {
        fetchEmployees();
        if (isEditing) {
            fetchExistingRecord();
        }
    }, [id]);

    useEffect(() => {
        if (formData.ewc && formData.intended_start_date) {
            calculateDates();
        }
    }, [formData.ewc, formData.intended_start_date]);

    const fetchEmployees = async () => {
        try {
            const res = await api.get('/employees');
            setEmployees(res.data.employees || []);
        } catch (err) {
            console.error('Failed to fetch employees', err);
        }
    };

    const fetchExistingRecord = async () => {
        try {
            const res = await api.get(`/maternity/${id}`);
            setFormData({
                employee_id: res.data.employee_id,
                ewc: res.data.ewc,
                notification_date: res.data.notification_date || '',
                intended_start_date: res.data.intended_start_date || '',
                actual_birth_date: res.data.actual_birth_date || ''
            });
        } catch (err) {
            setError('Failed to load record');
        }
    };

    const calculateDates = async () => {
        if (!formData.ewc) return;
        
        try {
            const res = await api.post('/maternity/calculate-dates', {
                ewc: formData.ewc,
                intended_start_date: formData.intended_start_date || null
            });
            setCalculatedDates(res.data);
        } catch (err) {
            console.error('Failed to calculate dates', err);
        }
    };

    const handleNext = () => {
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);
        
        try {
            if (isEditing) {
                await api.put(`/maternity/${id}`, formData);
            } else {
                await api.post('/maternity', formData);
            }
            navigate('/team/maternity');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save record');
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const selectedEmployee = employees.find(e => e.id === parseInt(formData.employee_id));

    const canProceed = () => {
        switch (activeStep) {
            case 0: return formData.employee_id;
            case 1: return formData.ewc;
            case 2: return formData.notification_date;
            case 3: return formData.intended_start_date;
            case 4: return true;
            default: return false;
        }
    };

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            Select Employee
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Choose the employee going on maternity leave
                        </Typography>
                        <TextField
                            select
                            fullWidth
                            label="Employee"
                            value={formData.employee_id}
                            onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                            sx={{ mb: 2 }}
                        >
                            <MenuItem value="">Select an employee...</MenuItem>
                            {employees.map((emp) => (
                                <MenuItem key={emp.id} value={emp.id}>
                                    {emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim()}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                );

            case 1:
                return (
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            Expected Week of Childbirth
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Enter the expected due date. All statutory dates are calculated from this.
                        </Typography>
                        <TextField
                            type="date"
                            fullWidth
                            label="Expected Due Date"
                            value={formData.ewc}
                            onChange={(e) => setFormData({ ...formData, ewc: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            sx={{ mb: 2 }}
                        />
                        {calculatedDates && (
                            <Card variant="outlined" sx={{ bgcolor: '#f5f3ff', mt: 2 }}>
                                <CardContent>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#7c3aed', mb: 1 }}>
                                        Calculated Dates
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Earliest Start</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {formatDate(calculatedDates.earliest_start)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Qualifying Week</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {formatDate(calculatedDates.qualifying_week)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        )}
                    </Box>
                );

            case 2:
                return (
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            Notification Date
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            The date the employee formally notified you of their pregnancy. 
                            You have 28 days from this date to respond (legal requirement).
                        </Typography>
                        <TextField
                            type="date"
                            fullWidth
                            label="Date Notified"
                            value={formData.notification_date}
                            onChange={(e) => setFormData({ ...formData, notification_date: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            sx={{ mb: 2 }}
                        />
                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                <strong>Important:</strong> You must write to the employee within 28 days 
                                confirming their expected return date.
                            </Typography>
                        </Alert>
                    </Box>
                );

            case 3:
                return (
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            Intended Start Date
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            When does the employee intend to start their maternity leave?
                        </Typography>
                        <TextField
                            type="date"
                            fullWidth
                            label="Start Date"
                            value={formData.intended_start_date}
                            onChange={(e) => setFormData({ ...formData, intended_start_date: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                                min: calculatedDates?.earliest_start || ''
                            }}
                            sx={{ mb: 2 }}
                        />
                        {calculatedDates && (
                            <Card variant="outlined" sx={{ bgcolor: '#f5f3ff', mt: 2 }}>
                                <CardContent>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#7c3aed', mb: 2 }}>
                                        Leave Period
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6} md={3}>
                                            <Typography variant="caption" color="text.secondary">OML Ends</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {formatDate(calculatedDates.oml_end)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">26 weeks</Typography>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Typography variant="caption" color="text.secondary">AML Ends</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {formatDate(calculatedDates.aml_end)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">52 weeks</Typography>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Typography variant="caption" color="text.secondary">Extension Deadline</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {formatDate(calculatedDates.extension_deadline)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">8 weeks before OML</Typography>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Typography variant="caption" color="text.secondary">Expected Return</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {formatDate(calculatedDates.expected_return)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        )}
                    </Box>
                );

            case 4:
                return (
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            Review & Confirm
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Please review the details before saving
                        </Typography>

                        <Card variant="outlined" sx={{ mb: 3 }}>
                            <CardContent>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                            <Box sx={{ 
                                                width: 48, 
                                                height: 48, 
                                                borderRadius: 2, 
                                                bgcolor: '#f5f3ff', 
                                                color: '#8b5cf6',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: 600,
                                                fontSize: 18
                                            }}>
                                                {selectedEmployee?.name?.charAt(0) || 'E'}
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                    {selectedEmployee?.name || 'Selected Employee'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {selectedEmployee?.email}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Divider sx={{ my: 2 }} />
                                    </Grid>

                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary">Due Date</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {formatDate(formData.ewc)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary">Notification Date</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {formatDate(formData.notification_date)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary">Leave Starts</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {formatDate(formData.intended_start_date)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary">Expected Return</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {formatDate(calculatedDates?.expected_return)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        <Alert severity="warning">
                            <Typography variant="body2">
                                <strong>Next Steps:</strong> After saving, you'll need to send the acknowledgement 
                                letter within 28 days confirming the expected return date.
                            </Typography>
                        </Alert>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Box>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                    color: 'white',
                    p: 4,
                    mb: 4,
                    borderRadius: 0
                }}
            >
                <Container maxWidth="md">
                    <Button
                        startIcon={<BackIcon />}
                        onClick={() => navigate('/team/maternity')}
                        sx={{ color: 'white', mb: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                    >
                        Back to Tracker
                    </Button>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ 
                            width: 56, 
                            height: 56, 
                            borderRadius: 2, 
                            bgcolor: 'rgba(255,255,255,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <MaternityIcon sx={{ fontSize: 28 }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                                {isEditing ? 'Edit Maternity Record' : 'New Maternity Record'}
                            </Typography>
                            <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                {isEditing ? 'Update leave details' : 'Record a new maternity leave notification'}
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </Paper>

            <Container maxWidth="md">
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
                )}

                {/* Stepper */}
                <Card elevation={0} sx={{ border: '1px solid #e5e7eb', mb: 4 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Stepper activeStep={activeStep} alternativeLabel>
                            {steps.map((step, index) => (
                                <Step key={step.label}>
                                    <StepLabel
                                        StepIconComponent={() => (
                                            <Box
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: 1.5,
                                                    bgcolor: activeStep >= index ? '#8b5cf6' : '#e5e7eb',
                                                    color: activeStep >= index ? 'white' : '#9ca3af',
                                                    fontSize: 14,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {activeStep > index ? <CheckIcon fontSize="small" /> : index + 1}
                                            </Box>
                                        )}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: activeStep === index ? 600 : 400,
                                                color: activeStep >= index ? '#8b5cf6' : 'text.secondary'
                                            }}
                                        >
                                            {step.label}
                                        </Typography>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </CardContent>
                </Card>

                {/* Step Content */}
                <Card elevation={0} sx={{ border: '1px solid #e5e7eb', mb: 4 }}>
                    <CardContent sx={{ p: 4 }}>
                        {renderStepContent()}
                    </CardContent>
                </Card>

                {/* Navigation Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Button
                        variant="outlined"
                        onClick={handleBack}
                        disabled={activeStep === 0}
                        startIcon={<BackIcon />}
                        sx={{ borderColor: '#e5e7eb', color: 'text.secondary' }}
                    >
                        Back
                    </Button>

                    {activeStep === steps.length - 1 ? (
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
                            sx={{
                                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                                '&:hover': { opacity: 0.9 }
                            }}
                        >
                            {loading ? 'Saving...' : 'Save Record'}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={!canProceed()}
                            endIcon={<NextIcon />}
                            sx={{
                                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                                '&:hover': { opacity: 0.9 }
                            }}
                        >
                            Continue
                        </Button>
                    )}
                </Box>
            </Container>
        </Box>
    );
};

export default MaternityForm;